import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle } from '@mui/material';
import { ArrowUpRight, RefreshCw, Thermometer } from 'lucide-react';
import useAuthContext from '../../hooks/useAuthContext';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { SensorDevicesService } from '../../hooks/SensorDevicesService';
import {
  DiscoverSensorDevicesResult,
  SENSOR_DEVICE_NO_PROVIDER_CODE,
  SENSOR_PROVIDER_TYPE,
  SensorDevice,
  SensorProviderStatus,
  isBindable
} from '../../models/sensor-devices';
import { extractApiErrorCode, extractApiErrorMessage, notifications } from '../../utils';
import { Button } from '../../elements/ui/button';
import { Card } from '../../elements/ui/card';
import { Skeleton } from '../../elements/ui/skeleton';
import SensorDeviceLocationDialog from './SensorDeviceLocationDialog';
import SensorDevicesTable from './SensorDevicesTable';

/** What discover just did. Kept on the page rather than toasted - a created pen needs renaming, not 4 seconds. */
interface DiscoverOutcome {
  result: DiscoverSensorDevicesResult | null;
  errorMessage: string | null;
  /** The farm has no enabled Whysor provider, so the fix is one panel up rather than a retry. */
  isMissingProvider: boolean;
}

/** Mirrors the table's row rhythm so nothing jumps when the devices land. */
const TableSkeleton: React.FC = () => (
  <div className="divide-y divide-gray-100 border-t border-gray-200">
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex items-center gap-4 px-6 py-3">
        <div className="w-56 shrink-0">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-1.5 h-3 w-28" />
        </div>
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="ml-auto h-8 w-8 rounded-md" />
      </div>
    ))}
  </div>
);

/**
 * The farm's sensor devices, and the pen each one is bound to.
 *
 * This screen exists because no provider knows where a device physically hangs. Whysor hands over the
 * customer's organization and a free-text name somebody typed there - nothing about rooms or pens - so the
 * backend binds each newly discovered device to an *arbitrary* free pen. Every pen therefore shows a real but
 * arbitrarily attributed device until a person who has walked the barn corrects it here.
 *
 * Two things about refreshing are worth stating rather than leaving to be discovered:
 *
 * - The sync worker is a daily cron job, so a farm that enabled Whysor an hour ago sees nothing until tomorrow
 *   unless it asks. "Refresh devices" asks the provider now.
 * - Discovery registers and binds devices only. It imports **no readings** - a two-year backfill has no
 *   business inside a request - so a device can appear here immediately while its climate charts stay empty
 *   until the worker next runs. That is expected, and the page says so.
 */
const SensorDevices: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const confirmDialog = useConfirmation();
  const navigate = useNavigate();

  /**
   * The switched-to business unit when there is one, and otherwise the farm the token itself names.
   *
   * `selectedBusinessUnitId` comes from a `unitId` claim that only the `/admin` business-unit flow mints, so
   * on an ordinary farm login it is `''` - which is why every other farm-scoped screen reads `state.farmId`
   * (`FarmLocations`, `LocationsHierarchy`, `Silos`, `AnimalSearch`, and eight more). Reading only the
   * selected unit leaves the panel permanently empty with its actions disabled, which is what
   * `ExternalProviders` does today.
   */
  const farmId = state.selectedBusinessUnitId || state.farmId;

  const [devices, setDevices] = React.useState<SensorDevice[]>([]);
  /**
   * Null means "not answered", not "not configured".
   *
   * The difference decides what the panel is allowed to claim. `UNCONFIGURED_SENSOR_PROVIDER` is a finding -
   * the farm has no Whysor row - and the service returns it only when the list came back without one. A
   * failed request has found nothing out, so it stays null and the panel says nothing about the provider
   * rather than telling the reader to go and set up something that may already be set up.
   */
  const [providerStatus, setProviderStatus] = React.useState<SensorProviderStatus | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  /** True once a fetch has settled, successfully or not - lets the panel tell "empty" from "not asked yet". */
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  /** Discover calls an external provider, so it is slow and gets a busy flag of its own. */
  const [isDiscovering, setIsDiscovering] = React.useState(false);
  const [isSavingLocation, setIsSavingLocation] = React.useState(false);
  const [outcome, setOutcome] = React.useState<DiscoverOutcome | null>(null);
  const [editedDevice, setEditedDevice] = React.useState<SensorDevice | null>(null);
  const [showRetired, setShowRetired] = React.useState(false);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  React.useEffect(() => {
    if (!farmId) return;

    const service = new SensorDevicesService();
    // The farm can change under a refresh; ignore anything but the newest request.
    let cancelled = false;

    setIsBusy(true);

    // Both in one pass: the device list alone cannot distinguish "no devices yet" from "no provider", and
    // showing the table before the provider is known would flash the wrong empty state.
    Promise.all([service.getDevices(farmId), service.getProviderStatus(farmId)])
      .then(([result, provider]) => {
        if (cancelled) return;
        setDevices(result);
        setProviderStatus(provider);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setDevices([]);
        setProviderStatus(null);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [farmId, reloadToken, t]);

  const handleDiscover = async () => {
    if (!farmId || isDiscovering) return;

    setIsDiscovering(true);
    setOutcome(null);

    try {
      const result = await new SensorDevicesService().discover(farmId);
      setOutcome({ result, errorMessage: null, isMissingProvider: false });
      reload();
    } catch (e: unknown) {
      // The domain errors here are all actionable - no enabled provider, no organization name, provider
      // unreachable - and the backend words each one. A generic failure would throw that away.
      setOutcome({
        result: null,
        errorMessage: extractApiErrorMessage(e, { fallback: t('f-com-api-error') }),
        isMissingProvider: extractApiErrorCode(e) === SENSOR_DEVICE_NO_PROVIDER_CODE
      });
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleUnassign = (device: SensorDevice) => {
    if (!farmId) return;

    confirmDialog({
      dialogId: 'unassign-sensor-device-dialog',
      title: t('f-sensor-dev-unassign-title'),
      message: t('f-sensor-dev-unassign-msg', {
        name: device.externalName || t('f-com-unknown'),
        pen: [device.locationRoom, device.locationPen].filter((part) => !!part).join(' / ')
      }),
      alertSeverity: 'warning'
    })
      .then(async () => {
        setIsSavingLocation(true);

        try {
          await new SensorDevicesService().unassignLocation(farmId, device.id);
          notifications.success(t('f-sensor-dev-unassign-ok'));
          reload();
        } catch (e: unknown) {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        } finally {
          setIsSavingLocation(false);
        }
      })
      .catch(() => {
        // Dismissed - the confirmation service rejects on cancel.
      });
  };

  /**
   * Devices that need binding - not simply devices without a pen.
   *
   * Two kinds of device hold no pen and never will: a weather station, which is the wrong kind of thing, and
   * a retired one, which the provider has stopped listing. Counting either would put a number in front of the
   * reader that no amount of work can bring down to zero.
   */
  const unboundCount = devices.filter((device) => isBindable(device) && !device.locationId).length;

  /**
   * Retired devices are folded away by default.
   *
   * They are typically devices that were never this farm's - picked up under the organization and dropped
   * again by a later sync - so on the list a farmer works through they are noise. The backend keeps them so
   * the readings they took stay readable and so a pen going quiet has an explanation, which is a question
   * asked occasionally rather than a row to act on, so it lives behind a toggle instead of in the way.
   */
  const retiredCount = devices.filter((device) => device.isRetired).length;
  const rows = showRetired ? devices : devices.filter((device) => !device.isRetired);

  /**
   * Whether the provider is known to be broken.
   *
   * Read this way round on purpose: an unknown provider is not treated as a broken one, so a failed lookup
   * leaves the Refresh button enabled and the ordinary empty state in place. Letting the reader try and get
   * the backend's own 3005 is better than a screen that blocks the action on a fact it never established.
   */
  const providerProblemKey: string | null = !providerStatus
    ? null
    : !providerStatus.isConfigured
      ? 'f-sensor-dev-provider-missing'
      : !providerStatus.isEnabled
        ? 'f-sensor-dev-provider-disabled'
        : !providerStatus.organizationName
          ? 'f-sensor-dev-provider-no-org'
          : null;

  /** Takes the reader to the panel that fixes it, rather than telling them where it is. */
  const goToExternalProviders = () => navigate('/farm-settings/settings');

  /**
   * Shown whenever the provider cannot work - but never instead of the device table.
   *
   * A farm that switched Whysor off still has the devices its last sync found, and they are still bound to
   * pens that still have climate history. Hiding them would lose real information to explain a
   * configuration problem, so this sits above the table rather than replacing it.
   */
  const renderProviderCallout = () => {
    if (!loaded || !providerProblemKey) return null;

    return (
      <Alert
        severity="warning"
        className="mx-5 mb-4"
        action={
          <Button variant="outline" size="sm" onClick={goToExternalProviders} className="whitespace-nowrap">
            {t('f-sensor-dev-provider-open-settings')}
            <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        }
      >
        <AlertTitle className="!text-sm">{t(providerProblemKey)}</AlertTitle>
        <p className="text-xs">{t('f-sensor-dev-provider-callout-hint')}</p>
      </Alert>
    );
  };

  const renderOutcome = () => {
    if (!outcome) return null;

    if (outcome.errorMessage) {
      return (
        <Alert severity="error" className="mx-5 mb-4">
          {outcome.errorMessage}
          {outcome.isMissingProvider && <p className="mt-1 text-xs">{t('f-sensor-dev-discover-no-provider-hint')}</p>}
        </Alert>
      );
    }

    if (!outcome.result) return null;

    const { devicesFound, devicesRegistered, locationsCreated } = outcome.result;

    return (
      <Alert severity={devicesFound === 0 ? 'warning' : 'success'} className="mx-5 mb-4">
        <AlertTitle className="!text-sm">
          {devicesFound === 0
            ? t('f-sensor-dev-discover-none')
            : t('f-sensor-dev-discover-ok', { found: devicesFound, registered: devicesRegistered })}
        </AlertTitle>

        {/* The backend had no free pen to bind a device to and made one. It is named after the device and
            marked Unknown, so somebody has to go and give it a real room and pen. */}
        {locationsCreated > 0 && (
          <p className="text-xs">{t('f-sensor-dev-discover-locations', { count: locationsCreated })}</p>
        )}

        {/* Registration and readings arrive by different routes, and the gap between them looks like a bug. */}
        {devicesRegistered > 0 && <p className="mt-1 text-xs">{t('f-sensor-dev-no-readings-hint')}</p>}
      </Alert>
    );
  };

  const renderBody = () => {
    if (!loaded) {
      return <TableSkeleton />;
    }

    if (devices.length === 0) {
      // With no provider the callout above is already the empty state, and it carries the only action that
      // can help. A second panel offering a Refresh that is certain to fail would be worse than nothing.
      if (providerProblemKey) return null;

      return (
        <div className="border-t border-gray-200 px-5 py-10 text-center">
          <Thermometer className="mx-auto h-8 w-8 text-gray-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-medium text-gray-900">{t('f-sensor-dev-empty-title')}</p>
          <p className="mx-auto mt-1 max-w-md text-xs text-gray-500">{t('f-sensor-dev-empty-body')}</p>

          {/* Discovery is the primary action, not a hint: the alternative is waiting until tomorrow. */}
          <Button className="mt-4" onClick={handleDiscover} disabled={isDiscovering || !farmId}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isDiscovering ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('f-sensor-dev-refresh')}
          </Button>

          <p className="mx-auto mt-3 max-w-md text-xs text-gray-400">{t('f-sensor-dev-empty-providers')}</p>
        </div>
      );
    }

    return (
      <>
        {(unboundCount > 0 || retiredCount > 0) && (
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 px-5 py-3">
            {unboundCount > 0 && (
              <p className="text-xs text-gray-500">{t('f-sensor-dev-unbound-summary', { count: unboundCount })}</p>
            )}

            {/* Offered only when there is something to unfold, the way the unsynced-device card does it - on a
                farm the sync has retired nothing on, the toggle would change nothing. */}
            {retiredCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => setShowRetired((open) => !open)}
                aria-expanded={showRetired}
              >
                {t(showRetired ? 'f-sensor-dev-hide-retired' : 'f-sensor-dev-show-retired', { count: retiredCount })}
              </Button>
            )}
          </div>
        )}

        {rows.length === 0 ? (
          <p className="border-t border-gray-200 px-5 py-8 text-center text-xs text-gray-500">
            {t('f-sensor-dev-only-retired')}
          </p>
        ) : (
          <SensorDevicesTable
            devices={rows}
            isBusy={isBusy || isSavingLocation}
            onEditLocation={setEditedDevice}
            onUnassignLocation={handleUnassign}
          />
        )}
      </>
    );
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{t('f-sensor-dev-title')}</h3>
          <p className="mt-1 max-w-2xl text-xs text-gray-500">{t('f-sensor-dev-desc')}</p>

          {/* Which service these devices come from, and whether it is actually switched on. Named rather
              than implied: "Whysor" is what the farmer configured and what their own devices are registered
              against, and the state of it decides whether anything on this screen can be refreshed. */}
          {loaded && providerStatus && (
            <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
              <span className="font-medium text-gray-700">{SENSOR_PROVIDER_TYPE}</span>

              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  providerProblemKey ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                }`}
              >
                {t(providerProblemKey ? 'f-sensor-dev-provider-off' : 'f-sensor-dev-provider-on')}
              </span>

              {providerStatus.organizationName && (
                <span>{t('f-sensor-dev-provider-org', { org: providerStatus.organizationName })}</span>
              )}

              {/* The panel that changes any of the above, one click away rather than described in prose.
                  `ArrowUpRight`, not an external-link glyph: this navigates to another tab of this same
                  page, and the external-link icon would promise a new window it does not open.
                  Dropped when the callout is up - that carries the same button as its primary action, and
                  two of them a centimetre apart reads as two different destinations. */}
              {!providerProblemKey && (
                <Button variant="outline" size="sm" onClick={goToExternalProviders}>
                  {t('f-sensor-dev-provider-open-settings')}
                  <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              )}
            </p>
          )}
        </div>

        {/* Disabled when the provider cannot answer: pressing it would spend a round trip on an external
            service to be told what the callout below already says. */}
        <Button
          variant="outline"
          onClick={handleDiscover}
          disabled={isDiscovering || !farmId || !!providerProblemKey}
          title={t(providerProblemKey ? 'f-sensor-dev-refresh-blocked' : 'f-sensor-dev-refresh-hint')}
        >
          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isDiscovering ? 'animate-spin' : ''}`} aria-hidden="true" />
          {t('f-sensor-dev-refresh')}
        </Button>
      </div>

      {renderProviderCallout()}
      {renderOutcome()}
      {renderBody()}

      {editedDevice && farmId && (
        <SensorDeviceLocationDialog
          device={editedDevice}
          devices={devices}
          farmId={farmId}
          onClose={() => setEditedDevice(null)}
          onSaved={() => {
            setEditedDevice(null);
            reload();
          }}
        />
      )}
    </Card>
  );
};

export default SensorDevices;
